import { OrderMethods, type IntegrationPartner } from '@centric-os/types';
import { defineStore, storeToRefs, type StateTree } from 'pinia';
import { useStationStore } from '../use-station-store';
import { useStoreAPIAction } from '@centric-os/stores/plugins/api';
import { isEmpty, isNil } from 'lodash-es';

interface State extends StateTree {
  integration: IntegrationPartner;
  isMobileIntegrated: boolean;
  editConfig: boolean;
  /**
   * Bumped when the user cancels an edit. Sections that must discard draft field
   * state (e.g. robot travel times) watch this instead of `editConfig`, so a
   * successful Save — which also clears `editConfig` — does not re-seed from
   * possibly stale store data.
   */
  configCancelCount: number;
  /**
   * Has the user opted in to configuring station level meal plan?
   */
  mealPlanActive: boolean;
}

export function useAppConfigStore(storeId = 'appConfig') {
  const store = defineStore(storeId, {
    state: (): State => ({
      integration: null,
      isMobileIntegrated: false,
      editConfig: false,
      configCancelCount: 0,
      mealPlanActive: false,
    }),
    actions: {},
    getters: {
      /*
       * Get permission to edit a configuration, if is first time setup or edit is enabled
       * @returns {boolean}
       */
      canEditConfig(state: State): boolean {
        // Set store for station
        const stationStore = useStationStore();
        const { loading: saving } = useStoreAPIAction(
          stationStore,
          'updateStationAndConfigurations',
        );

        return (state.editConfig || !state.isMobileIntegrated) && !saving.value;
      },

      /**
       * Determines the station's order method based on the current station configuration.
       *
       * @param {State} state - The current state of the store.
       * @returns {OrderMethods | null} - The identified order method or `null` if no valid configuration is found.
       */
      getStationOrderMethod(state: State): OrderMethods | null {
        // Set store for station
        const stationStore = useStationStore();
        const { stationBrand } = storeToRefs(stationStore);

        const is = stationBrand.value?.is;

        // Return null if `is` is null, undefined, or empty
        if (isNil(is) && isEmpty(is)) {
          return null;
        }

        // Determine the order method based on station configuration
        if (is.scan_and_go_supported) {
          return OrderMethods.SCAN_N_PAY;
        } else if (is.frictionless_supported) {
          return OrderMethods.JWO;
        } else {
          return OrderMethods.PRE_ORDER;
        }

        // Return null if no condition matches
        return null;
      },
    },
    pagination: false,
  });

  return store();
}
